import React, { useState } from 'react';
import { Card, Input } from '../ui';
import { Heart, Clock, DollarSign, TrendingUp, HelpCircle, Briefcase, Monitor, Users, ExternalLink, Calculator as CalculatorIcon, Car, ShoppingBag, Target, Percent, Printer } from 'lucide-react';
import { cn } from '../../lib/utils';
import { motion } from 'framer-motion';

const STATE_RATES: Record<string, number> = {
  "National Average": 34.79,
  "Alabama": 30.59,
  "Alaska": 37.13,
  "Arizona": 33.70,
  "Arkansas": 29.09,
  "California": 40.14,
  "Colorado": 38.74,
  "Connecticut": 38.43,
  "Delaware": 32.21,
  "District of Columbia": 52.06,
  "Florida": 33.00,
  "Georgia": 32.63,
  "Hawaii": 37.43,
  "Idaho": 32.48,
  "Illinois": 34.29,
  "Indiana": 31.05,
  "Iowa": 29.96,
  "Kansas": 30.86,
  "Kentucky": 29.47,
  "Louisiana": 29.14,
  "Maine": 32.13,
  "Maryland": 35.53,
  "Massachusetts": 42.00,
  "Michigan": 32.87,
  "Minnesota": 38.16,
  "Mississippi": 27.01,
  "Missouri": 32.07,
  "Montana": 32.15,
  "Nebraska": 31.96,
  "Nevada": 30.86,
  "New Hampshire": 35.76,
  "New Jersey": 36.97,
  "New Mexico": 28.69,
  "New York": 38.48,
  "North Carolina": 33.01,
  "North Dakota": 34.40,
  "Ohio": 32.42,
  "Oklahoma": 30.63,
  "Oregon": 36.44,
  "Pennsylvania": 32.28,
  "Rhode Island": 36.34,
  "South Carolina": 31.16,
  "South Dakota": 31.19,
  "Tennessee": 30.55,
  "Texas": 33.59,
  "Utah": 34.45,
  "Vermont": 34.39,
  "Virginia": 34.42,
  "Washington": 41.70,
  "West Virginia": 29.13,
  "Wisconsin": 34.00,
  "Wyoming": 31.69
};

export function VolunteerValueCalculator() {
  const [calcMode, setCalcMode] = useState<'total' | 'headcount'>('total');
  const [selectedState, setSelectedState] = useState('National Average');

  // Top Level
  const [projectName, setProjectName] = useState('Annual Volunteer Report');

  // General Tier
  const [generalHours, setGeneralHours] = useState('100');
  const [generalHeadcount, setGeneralHeadcount] = useState('10');
  const [generalHoursPer, setGeneralHoursPer] = useState('10');
  const [generalRate, setGeneralRate] = useState(STATE_RATES['National Average'].toString());

  // Professional Tier
  const [profHours, setProfHours] = useState('20');
  const [profHeadcount, setProfHeadcount] = useState('2');
  const [profHoursPer, setProfHoursPer] = useState('10');
  const [profRate, setProfRate] = useState('150');

  // Technical Tier
  const [techHours, setTechHours] = useState('10');
  const [techHeadcount, setTechHeadcount] = useState('1');
  const [techHoursPer, setTechHoursPer] = useState('10');
  const [techRate, setTechRate] = useState('85');

  // Additional Expenses
  const [milesDriven, setMilesDriven] = useState('150');
  const [inKindGoods, setInKindGoods] = useState('200');

  // Overhead/Fringe
  const [useFringe, setUseFringe] = useState(false);
  const [fringePercent, setFringePercent] = useState('15');

  // Impact Translator
  const [metricName, setMetricName] = useState('Meals Served');
  const [metricCost, setMetricCost] = useState('5');

  // Sync general rate when the selected state changes (render-phase adjustment).
  const [prevRateState, setPrevRateState] = useState(selectedState);
  if (selectedState !== prevRateState) {
    setPrevRateState(selectedState);
    setGeneralRate(STATE_RATES[selectedState].toString());
  }

  // Derived Values
  const gH = calcMode === 'total' 
    ? (parseFloat(generalHours) || 0) 
    : ((parseFloat(generalHeadcount) || 0) * (parseFloat(generalHoursPer) || 0));
    
  const pH = calcMode === 'total' 
    ? (parseFloat(profHours) || 0) 
    : ((parseFloat(profHeadcount) || 0) * (parseFloat(profHoursPer) || 0));
    
  const tH = calcMode === 'total' 
    ? (parseFloat(techHours) || 0) 
    : ((parseFloat(techHeadcount) || 0) * (parseFloat(techHoursPer) || 0));
  
  const gR = parseFloat(generalRate) || 0;
  const pR = parseFloat(profRate) || 0;
  const tR = parseFloat(techRate) || 0;

  const generalValue = gH * gR;
  const profValue = pH * pR;
  const techValue = tH * tR;

  const baseTimeValue = generalValue + profValue + techValue;
  const totalHours = gH + pH + tH;

  const mileageValue = (parseFloat(milesDriven) || 0) * 0.14; // IRS 2024 charity rate
  const goodsValue = parseFloat(inKindGoods) || 0;
  
  const fringeValue = useFringe ? (baseTimeValue * ((parseFloat(fringePercent) || 0) / 100)) : 0;

  const totalValue = baseTimeValue + fringeValue + mileageValue + goodsValue;
  const blendedRate = totalHours > 0 ? (baseTimeValue / totalHours) : 0; // Blended hourly rate strictly based on time value

  const mCost = parseFloat(metricCost) || 0;
  const impactEquivalent = mCost > 0 ? Math.floor(totalValue / mCost) : 0;

  const formatCurrency = (val: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);

  return (
    <div className="text-left font-sans max-w-5xl mx-auto space-y-8 pb-12">
      {/* Header Banner */}
      <div className="bg-black text-white p-8 border-[8px] border-black dark:border-white shadow-[12px_12px_0px_#FFD600] flex flex-col md:flex-row items-center justify-between gap-6 overflow-hidden relative">
        <div className="absolute top-0 right-0 -mr-16 -mt-16 w-32 h-32 bg-[#FFD600] rounded-none pointer-events-none" />
        <div className="absolute bottom-0 left-0 -ml-16 -mb-16 w-32 h-32 bg-[#0044FF] rounded-none pointer-events-none" />
        
        <div className="relative z-10 w-full flex-1">
          <div className="flex items-center gap-3 mb-4">
            <Heart className="w-8 h-8 text-[#FFD600] shrink-0" />
            <Input 
              type="text" 
              value={projectName} 
              onChange={e => setProjectName(e.target.value)} 
              placeholder="Project or Grant Name"
              className="text-3xl md:text-5xl font-black font-display uppercase tracking-tighter bg-transparent border-b-4 border-white focus:border-[#FFD600] focus:ring-0 rounded-none px-0 text-white placeholder-white/50 h-auto py-1 shadow-none"
            />
          </div>
          <p className="text-white font-bold max-w-xl text-lg uppercase tracking-wider">
            Quantify the true financial impact of your volunteers. Includes in-kind goods, mileage, and customizable overhead rates for comprehensive grant reporting.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-center gap-3 relative z-10">
          <button
            onClick={() => window.print()}
            className="bg-[#FFD600] hover:bg-yellow-400 text-black font-black uppercase text-xs tracking-wider px-6 py-3 border-[2px] border-black shadow-[4px_4px_0px_#0044FF] flex items-center gap-2 cursor-pointer rounded-none shrink-0"
          >
            <Printer className="w-4 h-4" />
            Print In-Kind Grant Report
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-8">
        {/* Left Column: Inputs */}
        <div className="lg:col-span-5 space-y-6">
          <Card className="p-6 border-[4px] border-black dark:border-white bg-white dark:bg-black shadow-[8px_8px_0px_#000000] dark:shadow-[8px_8px_0px_#ffffff] rounded-none">
             <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4 mb-6 border-b border-slate-100 dark:border-slate-800 pb-4">
               <h3 className="text-lg font-bold flex items-center gap-2">
                 <Clock className="text-teal-500" size={20} /> Time Log
               </h3>
               
               {/* Mode Toggle */}
               <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-lg shrink-0">
                 <button
                   onClick={() => setCalcMode('total')}
                   className={cn(
                     "px-3 py-1.5 text-xs font-bold rounded-md transition-colors",
                     calcMode === 'total' ? "bg-white dark:bg-slate-700 text-teal-600 dark:text-teal-400 shadow-sm" : "text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                   )}
                 >
                   Total Hours
                 </button>
                 <button
                   onClick={() => setCalcMode('headcount')}
                   className={cn(
                     "px-3 py-1.5 text-xs font-bold rounded-md transition-colors",
                     calcMode === 'headcount' ? "bg-white dark:bg-slate-700 text-teal-600 dark:text-teal-400 shadow-sm" : "text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                   )}
                 >
                   By Headcount
                 </button>
               </div>
             </div>

             <div className="space-y-6">
               
               {/* General Volunteer Tier */}
               <div className="space-y-3 bg-teal-50/50 dark:bg-teal-900/10 p-4 rounded-xl border border-teal-100 dark:border-teal-900/30">
                 <div className="flex items-center gap-2 text-slate-800 dark:text-slate-200 font-bold mb-2">
                   <Users className="w-4 h-4 text-emerald-500" />
                   General Volunteers
                 </div>
                 
                 <div className="mb-4">
                   <label className="text-[10px] uppercase font-bold text-slate-500 tracking-widest block mb-1">State / Region (2024 Rates)</label>
                   <select 
                     value={selectedState} 
                     onChange={(e) => setSelectedState(e.target.value)}
                     className="w-full bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                   >
                     {Object.keys(STATE_RATES).map(state => (
                       <option key={state} value={state}>{state} (${STATE_RATES[state]})</option>
                     ))}
                   </select>
                 </div>

                 <div className="grid grid-cols-2 gap-4">
                   {calcMode === 'total' ? (
                     <div className="col-span-2 sm:col-span-1">
                       <label className="text-[10px] uppercase font-bold text-slate-500 tracking-widest block mb-1">Total Hours</label>
                       <Input type="number" value={generalHours} onChange={e => setGeneralHours(e.target.value)} className="bg-white dark:bg-slate-950" />
                     </div>
                   ) : (
                     <>
                       <div>
                         <label className="text-[10px] uppercase font-bold text-slate-500 tracking-widest block mb-1">Headcount</label>
                         <Input type="number" value={generalHeadcount} onChange={e => setGeneralHeadcount(e.target.value)} className="bg-white dark:bg-slate-950" />
                       </div>
                       <div>
                         <label className="text-[10px] uppercase font-bold text-slate-500 tracking-widest block mb-1">Hrs/Person</label>
                         <Input type="number" value={generalHoursPer} onChange={e => setGeneralHoursPer(e.target.value)} className="bg-white dark:bg-slate-950" />
                       </div>
                     </>
                   )}
                   <div className={calcMode === 'total' ? "col-span-2 sm:col-span-1" : "col-span-2"}>
                     <label className="text-[10px] uppercase font-bold text-slate-500 tracking-widest flex items-center justify-between mb-1">
                       Rate ($)
                     </label>
                     <Input type="number" step="0.1" value={generalRate} onChange={e => setGeneralRate(e.target.value)} className="bg-white dark:bg-slate-950 border-teal-200 dark:border-teal-900" />
                   </div>
                 </div>
               </div>
               
               {/* Professional / Legal Tier */}
               <div className="space-y-3 pt-2">
                 <div className="flex items-center gap-2 text-slate-800 dark:text-slate-200 font-bold mb-1">
                   <Briefcase className="w-4 h-4 text-blue-500" />
                   Professional / Legal (Pro Bono)
                 </div>
                 <div className="grid grid-cols-2 gap-4">
                   {calcMode === 'total' ? (
                     <div className="col-span-2 sm:col-span-1">
                       <label className="text-[10px] uppercase font-bold text-slate-500 tracking-widest block mb-1">Total Hours</label>
                       <Input type="number" value={profHours} onChange={e => setProfHours(e.target.value)} className="bg-slate-50 dark:bg-slate-950" />
                     </div>
                   ) : (
                     <>
                       <div>
                         <label className="text-[10px] uppercase font-bold text-slate-500 tracking-widest block mb-1">Headcount</label>
                         <Input type="number" value={profHeadcount} onChange={e => setProfHeadcount(e.target.value)} className="bg-slate-50 dark:bg-slate-950" />
                       </div>
                       <div>
                         <label className="text-[10px] uppercase font-bold text-slate-500 tracking-widest block mb-1">Hrs/Person</label>
                         <Input type="number" value={profHoursPer} onChange={e => setProfHoursPer(e.target.value)} className="bg-slate-50 dark:bg-slate-950" />
                       </div>
                     </>
                   )}
                   <div className={calcMode === 'total' ? "col-span-2 sm:col-span-1" : "col-span-2"}>
                     <label className="text-[10px] uppercase font-bold text-slate-500 tracking-widest block mb-1">Market Rate ($)</label>
                     <Input type="number" value={profRate} onChange={e => setProfRate(e.target.value)} className="bg-slate-50 dark:bg-slate-950" />
                   </div>
                 </div>
               </div>

               {/* Technical / IT Tier */}
               <div className="space-y-3 pt-4 border-t border-slate-100 dark:border-slate-800">
                 <div className="flex items-center gap-2 text-slate-800 dark:text-slate-200 font-bold mb-1">
                   <Monitor className="w-4 h-4 text-purple-500" />
                   Technical / IT Specialists
                 </div>
                 <div className="grid grid-cols-2 gap-4">
                   {calcMode === 'total' ? (
                     <div className="col-span-2 sm:col-span-1">
                       <label className="text-[10px] uppercase font-bold text-slate-500 tracking-widest block mb-1">Total Hours</label>
                       <Input type="number" value={techHours} onChange={e => setTechHours(e.target.value)} className="bg-slate-50 dark:bg-slate-950" />
                     </div>
                   ) : (
                     <>
                       <div>
                         <label className="text-[10px] uppercase font-bold text-slate-500 tracking-widest block mb-1">Headcount</label>
                         <Input type="number" value={techHeadcount} onChange={e => setTechHeadcount(e.target.value)} className="bg-slate-50 dark:bg-slate-950" />
                       </div>
                       <div>
                         <label className="text-[10px] uppercase font-bold text-slate-500 tracking-widest block mb-1">Hrs/Person</label>
                         <Input type="number" value={techHoursPer} onChange={e => setTechHoursPer(e.target.value)} className="bg-slate-50 dark:bg-slate-950" />
                       </div>
                     </>
                   )}
                   <div className={calcMode === 'total' ? "col-span-2 sm:col-span-1" : "col-span-2"}>
                     <label className="text-[10px] uppercase font-bold text-slate-500 tracking-widest block mb-1">Market Rate ($)</label>
                     <Input type="number" value={techRate} onChange={e => setTechRate(e.target.value)} className="bg-slate-50 dark:bg-slate-950" />
                   </div>
                 </div>
               </div>
             </div>
          </Card>

          {/* Additional Expenses */}
          <Card className="p-6 border-[4px] border-black dark:border-white bg-white dark:bg-black shadow-[8px_8px_0px_#000000] dark:shadow-[8px_8px_0px_#ffffff] rounded-none">
            <h3 className="text-lg font-bold flex items-center gap-2 mb-6 border-b border-slate-100 dark:border-slate-800 pb-3">
              <ShoppingBag className="text-amber-500" size={20} /> Additional Expenses
            </h3>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center gap-1.5 mb-1">
                    <Car className="w-3.5 h-3.5 text-slate-500" />
                    <label className="text-[10px] uppercase font-bold text-slate-500 tracking-widest">Miles Driven</label>
                  </div>
                  <Input type="number" value={milesDriven} onChange={e => setMilesDriven(e.target.value)} className="bg-slate-50 dark:bg-slate-950" />
                  <p className="text-[9px] text-slate-400 mt-1 uppercase tracking-wider font-semibold">IRS Rate: 14¢ / mile</p>
                </div>
                <div>
                  <div className="flex items-center gap-1.5 mb-1">
                    <ShoppingBag className="w-3.5 h-3.5 text-slate-500" />
                    <label className="text-[10px] uppercase font-bold text-slate-500 tracking-widest">In-Kind Goods ($)</label>
                  </div>
                  <Input type="number" value={inKindGoods} onChange={e => setInKindGoods(e.target.value)} className="bg-slate-50 dark:bg-slate-950" placeholder="e.g. 50" />
                  <p className="text-[9px] text-slate-400 mt-1 uppercase tracking-wider font-semibold">Out-of-pocket supplies</p>
                </div>
              </div>

              <div className="pt-4 border-t border-slate-100 dark:border-slate-800">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 text-slate-800 dark:text-slate-200 font-bold">
                    <Percent className="w-4 h-4 text-indigo-500" />
                    Fringe Benefits & Overhead
                  </div>
                  <button 
                    onClick={() => setUseFringe(!useFringe)}
                    className={cn(
                      "relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none",
                      useFringe ? "bg-indigo-500" : "bg-slate-200 dark:bg-slate-700"
                    )}
                  >
                    <span className={cn(
                      "pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out",
                      useFringe ? "translate-x-4" : "translate-x-0"
                    )} />
                  </button>
                </div>
                {useFringe && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="flex gap-4 items-center bg-indigo-50/50 dark:bg-indigo-900/10 p-3 rounded-lg border border-indigo-100 dark:border-indigo-900/30"
                  >
                    <div className="flex-1">
                      <label className="text-[10px] uppercase font-bold text-indigo-800/70 dark:text-indigo-300 tracking-widest block mb-1">Fringe / Overhead Rate (%)</label>
                      <Input type="number" value={fringePercent} onChange={e => setFringePercent(e.target.value)} className="bg-white dark:bg-slate-950 border-indigo-200 dark:border-indigo-900" />
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          </Card>
          
          {/* Citation Info */}
          <p className="text-xs text-slate-500 dark:text-slate-400 flex items-start gap-2 mt-4 px-2">
            <HelpCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              <strong>Data Source:</strong> The 2024 value of a volunteer hour is provided by the <a href="https://independentsector.org/resource/value-of-volunteer-time/" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline inline-flex items-center gap-1">Independent Sector <ExternalLink className="w-3 h-3" /></a>, in partnership with the Do Good Institute at the University of Maryland. Mileage rate provided by the IRS 2024 charity standard.
            </span>
          </p>
        </div>

        {/* Right Column: Results */}
        <div className="lg:col-span-7 flex flex-col gap-6">
           {/* Total Card */}
           <Card className="p-8 bg-[#0044FF] border-[8px] border-black dark:border-white relative overflow-hidden shadow-[12px_12px_0px_#000000] dark:shadow-[12px_12px_0px_#ffffff] rounded-none flex-none text-white">
             <div className="flex justify-between items-start mb-6">
               <div>
                 <span className="text-xs font-black text-teal-800 dark:text-teal-400 uppercase tracking-widest">Total Estimated Value</span>
                 <p className="text-xs text-teal-600/70 mt-1 max-w-sm">
                   The comprehensive financial equivalent of your community's donated time, travel, and goods.
                 </p>
               </div>
               <DollarSign className="w-8 h-8 text-teal-500/30" />
             </div>
             
             <div className="mt-4 mb-8">
               <motion.span 
                 key={totalValue}
                 initial={{ opacity: 0, y: -10 }}
                 animate={{ opacity: 1, y: 0 }}
                 className="text-6xl md:text-7xl font-black font-display text-white tracking-tighter block"
               >
                 {formatCurrency(totalValue)}
               </motion.span>
             </div>
             
             <div className="grid grid-cols-2 gap-4 bg-white/60 dark:bg-black/20 p-4 rounded-xl border border-teal-100 dark:border-teal-900/50 backdrop-blur-sm">
               <div>
                 <span className="text-[10px] uppercase tracking-widest font-bold text-teal-700/70 dark:text-teal-400/70 block mb-1">Total Hours Logged</span>
                 <span className="text-xl font-bold text-teal-900 dark:text-teal-100 font-mono">{totalHours} <span className="text-sm opacity-50">hrs</span></span>
               </div>
               <div>
                 <span className="text-[10px] uppercase tracking-widest font-bold text-teal-700/70 dark:text-teal-400/70 block mb-1">Blended Base Rate</span>
                 <span className="text-xl font-bold text-teal-900 dark:text-teal-100 font-mono">{formatCurrency(blendedRate)} <span className="text-sm opacity-50">/hr</span></span>
               </div>
             </div>
           </Card>

           {/* Impact Translator Card */}
           <Card className="p-6 bg-[#FFD600] border-[4px] border-black dark:border-white shadow-[8px_8px_0px_#000000] dark:shadow-[8px_8px_0px_#ffffff] rounded-none text-black">
             <div className="flex justify-between items-center mb-4">
               <h3 className="text-lg font-bold flex items-center gap-2 text-rose-900 dark:text-rose-100">
                 <Target className="text-rose-500" size={20} /> Impact Translator
               </h3>
             </div>
             <p className="text-sm text-rose-700/80 dark:text-rose-300/80 mb-5">
               Translate your total dollar value into a real-world metric to show donors exactly what volunteer hours accomplish.
             </p>
             <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="text-[10px] uppercase font-bold text-rose-700/70 dark:text-rose-400/70 tracking-widest block mb-1">Core Metric (e.g. Meals Served)</label>
                  <Input type="text" value={metricName} onChange={e => setMetricName(e.target.value)} className="bg-white dark:bg-slate-950 border-rose-200 dark:border-rose-900" />
                </div>
                <div>
                  <label className="text-[10px] uppercase font-bold text-rose-700/70 dark:text-rose-400/70 tracking-widest block mb-1">Cost Per Unit ($)</label>
                  <Input type="number" value={metricCost} onChange={e => setMetricCost(e.target.value)} className="bg-white dark:bg-slate-950 border-rose-200 dark:border-rose-900" />
                </div>
             </div>
             
             <div className="bg-white/80 dark:bg-slate-950 p-6 rounded-xl border border-rose-100 dark:border-rose-900/50 text-center">
               <span className="block text-sm text-slate-500 font-bold uppercase tracking-wider mb-2">That's the financial equivalent of:</span>
               <span className="block text-4xl md:text-5xl font-black text-rose-600 dark:text-rose-400 tracking-tight">
                 {new Intl.NumberFormat('en-US').format(impactEquivalent)}
               </span>
               <span className="block text-lg font-bold text-rose-900 dark:text-rose-100 mt-1">
                 {metricName || 'Units'}
               </span>
             </div>
           </Card>

           {/* Breakdown Card */}
           <Card className="p-6 border-[4px] border-black dark:border-white bg-white dark:bg-black shadow-[8px_8px_0px_#000000] dark:shadow-[8px_8px_0px_#ffffff] rounded-none">
              <h4 className="text-sm font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider mb-6">Value Breakdown</h4>
              
              <div className="space-y-5">
                {/* General Bar */}
                <div>
                  <div className="flex justify-between items-end mb-2">
                    <span className="text-xs font-semibold text-slate-600 dark:text-slate-400 flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-emerald-500" /> General Labor</span>
                    <span className="text-sm font-bold font-mono">{formatCurrency(generalValue)}</span>
                  </div>
                  <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-2 overflow-hidden">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: `${totalValue > 0 ? (generalValue / totalValue) * 100 : 0}%` }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                      className="bg-emerald-500 h-full rounded-full" 
                    />
                  </div>
                </div>

                {/* Professional Bar */}
                <div>
                  <div className="flex justify-between items-end mb-2">
                    <span className="text-xs font-semibold text-slate-600 dark:text-slate-400 flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5 text-blue-500" /> Professional (Pro Bono)</span>
                    <span className="text-sm font-bold font-mono">{formatCurrency(profValue)}</span>
                  </div>
                  <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-2 overflow-hidden">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: `${totalValue > 0 ? (profValue / totalValue) * 100 : 0}%` }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                      className="bg-blue-500 h-full rounded-full" 
                    />
                  </div>
                </div>

                {/* Technical Bar */}
                <div>
                  <div className="flex justify-between items-end mb-2">
                    <span className="text-xs font-semibold text-slate-600 dark:text-slate-400 flex items-center gap-1.5"><Monitor className="w-3.5 h-3.5 text-purple-500" /> Technical / IT</span>
                    <span className="text-sm font-bold font-mono">{formatCurrency(techValue)}</span>
                  </div>
                  <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-2 overflow-hidden">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: `${totalValue > 0 ? (techValue / totalValue) * 100 : 0}%` }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                      className="bg-purple-500 h-full rounded-full" 
                    />
                  </div>
                </div>
                
                {/* Extras Bar (Mileage, Goods, Fringe) */}
                <div>
                  <div className="flex justify-between items-end mb-2">
                    <span className="text-xs font-semibold text-slate-600 dark:text-slate-400 flex items-center gap-1.5"><ShoppingBag className="w-3.5 h-3.5 text-amber-500" /> Fringe, Goods & Mileage</span>
                    <span className="text-sm font-bold font-mono">{formatCurrency(fringeValue + mileageValue + goodsValue)}</span>
                  </div>
                  <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-2 overflow-hidden">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: `${totalValue > 0 ? ((fringeValue + mileageValue + goodsValue) / totalValue) * 100 : 0}%` }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                      className="bg-amber-500 h-full rounded-full" 
                    />
                  </div>
                </div>

              </div>
           </Card>
        </div>
      </div>

      {/* PRINT-ONLY GRANT ATTACHMENT */}
      <div className="hidden print:block fixed inset-0 m-0 p-8 bg-white text-black z-[99999] font-sans">
        <style type="text/css" media="print">
          {`
            @page {
              size: portrait;
              margin: 10mm;
            }
            body {
              margin: 0;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              background-color: white !important;
            }
          `}
        </style>
        <div className="max-w-3xl mx-auto border-2 border-black p-8">
          <div className="border-b-4 border-[#0044FF] pb-4 mb-6 flex justify-between items-start">
            <div>
              <span className="text-[10px] font-mono font-black uppercase tracking-widest text-slate-500">
                IRS 501(c)(3) & GRANT IN-KIND MATCH DOCUMENTATION
              </span>
              <h1 className="text-3xl font-black font-display uppercase tracking-tight text-black mt-1">
                Volunteer In-Kind Valuation Report
              </h1>
              <p className="text-sm font-bold text-slate-700 mt-1">
                Project / Initiative: {projectName || 'Annual Volunteer Operations'}
              </p>
            </div>
            <div className="text-right font-mono text-xs">
              <span className="bg-black text-white px-2 py-1 font-bold">OFFICIAL REPORT</span>
              <p className="text-slate-500 mt-1">{new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 mb-8 bg-slate-50 border-2 border-black p-4">
            <div>
              <p className="text-[10px] font-mono uppercase text-slate-500 font-bold">Total Donated Hours</p>
              <p className="text-2xl font-black font-mono">{totalHours} hrs</p>
            </div>
            <div>
              <p className="text-[10px] font-mono uppercase text-slate-500 font-bold">Blended Hourly Rate</p>
              <p className="text-2xl font-black font-mono">{formatCurrency(blendedRate)}/hr</p>
            </div>
            <div>
              <p className="text-[10px] font-mono uppercase text-slate-500 font-bold">Total In-Kind Value</p>
              <p className="text-2xl font-black font-mono text-emerald-700">{formatCurrency(totalValue)}</p>
            </div>
          </div>

          <table className="w-full text-left border-collapse border border-black text-xs font-mono mb-8">
            <thead>
              <tr className="bg-slate-100 border-b border-black">
                <th className="p-2.5 border-r border-black font-bold uppercase">Contribution Category</th>
                <th className="p-2.5 border-r border-black font-bold uppercase text-right">Hours / Units</th>
                <th className="p-2.5 border-r border-black font-bold uppercase text-right">Valuation Rate</th>
                <th className="p-2.5 font-bold uppercase text-right">Total In-Kind Value</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-slate-300">
                <td className="p-2.5 border-r border-slate-300 font-bold">General Volunteer Service ({selectedState})</td>
                <td className="p-2.5 border-r border-slate-300 text-right">{gH} hrs</td>
                <td className="p-2.5 border-r border-slate-300 text-right">${generalRate}/hr</td>
                <td className="p-2.5 text-right font-bold">{formatCurrency(generalValue)}</td>
              </tr>
              <tr className="border-b border-slate-300">
                <td className="p-2.5 border-r border-slate-300 font-bold">Professional / Legal Services (Pro Bono)</td>
                <td className="p-2.5 border-r border-slate-300 text-right">{pH} hrs</td>
                <td className="p-2.5 border-r border-slate-300 text-right">${profRate}/hr</td>
                <td className="p-2.5 text-right font-bold">{formatCurrency(profValue)}</td>
              </tr>
              <tr className="border-b border-slate-300">
                <td className="p-2.5 border-r border-slate-300 font-bold">Technical / IT Specialist Services</td>
                <td className="p-2.5 border-r border-slate-300 text-right">{tH} hrs</td>
                <td className="p-2.5 border-r border-slate-300 text-right">${techRate}/hr</td>
                <td className="p-2.5 text-right font-bold">{formatCurrency(techValue)}</td>
              </tr>
              {useFringe && fringeValue > 0 && (
                <tr className="border-b border-slate-300">
                  <td className="p-2.5 border-r border-slate-300 font-bold">Fringe & Overhead ({fringePercent}%)</td>
                  <td className="p-2.5 border-r border-slate-300 text-right">—</td>
                  <td className="p-2.5 border-r border-slate-300 text-right">{fringePercent}%</td>
                  <td className="p-2.5 text-right font-bold">{formatCurrency(fringeValue)}</td>
                </tr>
              )}
              {mileageValue > 0 && (
                <tr className="border-b border-slate-300">
                  <td className="p-2.5 border-r border-slate-300 font-bold">Volunteer Travel Equivalent (IRS Rate)</td>
                  <td className="p-2.5 border-r border-slate-300 text-right">{milesDriven} miles</td>
                  <td className="p-2.5 border-r border-slate-300 text-right">$0.14/mi</td>
                  <td className="p-2.5 text-right font-bold">{formatCurrency(mileageValue)}</td>
                </tr>
              )}
              {goodsValue > 0 && (
                <tr className="border-b border-slate-300">
                  <td className="p-2.5 border-r border-slate-300 font-bold">Donated In-Kind Goods & Supplies</td>
                  <td className="p-2.5 border-r border-slate-300 text-right">Itemized</td>
                  <td className="p-2.5 border-r border-slate-300 text-right">Fair Market</td>
                  <td className="p-2.5 text-right font-bold">{formatCurrency(goodsValue)}</td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr className="bg-slate-100 font-black border-t-2 border-black text-sm">
                <td colSpan={3} className="p-2.5 text-right uppercase">Certified In-Kind Total:</td>
                <td className="p-2.5 text-right text-emerald-700">{formatCurrency(totalValue)}</td>
              </tr>
            </tfoot>
          </table>

          <div className="border-l-4 border-slate-400 pl-4 mb-8 text-xs text-slate-600 leading-relaxed font-sans">
            <strong>Methodology Note:</strong> Valuation benchmarks are derived from the official 2026 Independent Sector Value of Volunteer Time standard for {selectedState}. Pro bono professional and technical rates reflect prevailing fair-market wages in accordance with OMB Uniform Guidance (2 CFR 200.306) for Federal and private foundation match requirements.
          </div>

          <div className="pt-6 border-t border-slate-300 grid grid-cols-2 gap-8 text-xs font-sans">
            <div>
              <p className="font-bold mb-8">Attested By (Executive Director / Grant Administrator):</p>
              <div className="border-b-2 border-black w-64 mb-1"></div>
              <p className="text-slate-500 font-mono text-[10px]">Authorized Signature & Date</p>
            </div>
            <div className="text-right self-end font-mono text-[10px] text-slate-500">
              <p>Generated by OrgSets Community Toolbox</p>
              <p>https://orgsets.com/tools/volunteer-value-calculator</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
